import React, {FormEvent, useMemo, useState} from 'react';
import {
  Alert as MantineAlert,
  Box,
  Grid,
  TextInput as MantineTextInput,
  NumberInput as MantineNumberInput,
  Switch as MantineSwitch,
  Title,
  Divider, Text,
  Select as MantineSelect,
  SegmentedControl,
  Group
} from '@mantine/core';
import {Item, itemStore} from '../../../../../../redux/entities/inventory/item';
import {Form, Formik, FormikErrors} from 'formik';
import {propertyOf} from '../../../../../../util';
import {makeItem} from '../../../../../../redux/factory/inventory/item';
import {Dispatch, bindActionCreators} from 'redux';
import {AxiosError} from 'axios/index';
import {getErrorResponseMessage, handleAxiosError} from '../../../../../../util/http';
import {SubmitProps} from '../../../../../../types';
import {CommonState} from '../../../../../../redux';
import {connect} from 'react-redux';
import Select from 'react-select';
import {convertToDropDownOptions} from '../../../../../../util/form';
import {Category, categoryStore} from '../../../../../../redux/entities/inventory/category';
import {UpdateItemPictureRequest} from '../../../../../../components/util/ItemPicture/ItemPicture';
import {FormikState} from 'formik/dist/types';
import {ItemModalSchema} from './ItemModalSchema';
import {isNullOrWhitespace} from '../../../../../../util/string';
import { Link } from 'react-router-dom';
import { Order, orderStore } from '../../../../../../redux/entities/inventory/order';
import { format } from 'date-fns';
import StandardModal from '../../../../../../components/util/StandardModal/StandardModal';

// Helper to convert dollars to cents
const dollarsToCents = (dollars: number) => Math.round(dollars * 100);
// Helper to convert cents to dollars
const centsToDollars = (cents: number) => cents / 100;

export type UpsertItemRequest = Item & {updateItemPictureRequest?: UpdateItemPictureRequest};

type Props = SubmitProps<Item> & {
  show: boolean;
  itemId?: string;
  existing?: Item;
  selectable?: boolean;
  title?: string;
  predeterminedCategoryId?: string;
  readOnlyInitial?: boolean;
} & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const getFieldName = propertyOf<UpsertItemRequest>;

const ItemModal = (props: Props) => {
  const {show, existing, selectable = false, onSubmit, onCancel, categoryDropDownOptions, alphabeticallySortedItems, getItemById,
    title = `${existing ? 'Edit' : 'Create'} Inventory Item`, predeterminedCategoryId, readOnlyInitial, actions: {upsertItem}} = props;
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePictureRequest, setImagePictureRequest] = useState<UpdateItemPictureRequest | undefined>();
  const [searchValue, setSearchValue] = useState('');
  // Toggle between recent withdrawals and deposits lists
  const [recentTab, setRecentTab] = useState<'withdrawals' | 'deposits'>('withdrawals');
  // Read-only mode support
  const [isReadOnly, setIsReadOnly] = useState<boolean>(!!readOnlyInitial);
  const dropDownOptions = convertToDropDownOptions(alphabeticallySortedItems);

  const processSubmit = async (
    e: FormEvent<HTMLFormElement>,
    association: Item,
    validate: (values: Item) => Promise<FormikErrors<Item>>,
    formikHandleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void,
    resetForm: (nextState?: Partial<FormikState<Item>> | undefined) => void) => {
    setIsSubmitting(true);
    setErrorMessage('');
    e.persist();
    e.preventDefault();
    const errors = await validate(association);
    if (Object.values(errors).length !== 0) {
      formikHandleSubmit(e);
      setIsSubmitting(false);
    } else {
      try {
        const request = {...association, updateItemPictureRequest: imagePictureRequest};
        if (!predeterminedCategoryId)
          await upsertItem(request);
        onSubmit(request);
      } catch (e: AxiosError | any) {
        setErrorMessage(JSON.stringify(e));
      }
    }
    setIsSubmitting(false);
  };

  // Buttons are provided by StandardModal footer

  const recentOrdersForItem = useMemo(() => {
    const itemId = (existing?.id ?? '') as string;
    if (!itemId) return [] as (Order & {quantity: number; displayName: string})[];
    // Search all orders for ones that include this item
    const results: (Order & {quantity: number; displayName: string})[] = (props.orders ?? [])
      .map(o => {
        const oi = o.orderItems?.find(oi => oi.itemId === itemId);
        if (!oi) return null;
        const quantity = (oi.storeQuantity ?? 0) + (oi.warehouseQuantity ?? 0);
        const org = o.organization;
        const displayName = o.isWithdrawal ? (o.consumerInformation?.name ?? 'Unknown') : (o.organization ?? 'No Organization');
        return {...o, quantity, displayName};
      })
      .filter((x): x is Order & {quantity: number; displayName: string} => !!x)
      .sort((a, b) => (new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()))
      // Do not slice here; allow UI layer to filter and limit as needed
      ;
    return results;
  }, [props.orders, existing?.id]);

  const renderRecentOrders = () => {
    if (!existing?.id) return null;
    if (!recentOrdersForItem.length) return null;
    const q = (searchValue || '').trim().toLowerCase();
    const filtered = !q
      ? recentOrdersForItem
      : recentOrdersForItem.filter(o => {
          const dateStr = (() => {
            try { return format(new Date(o.submittedAt), 'M/d/yyyy h:mm a'); } catch { return ''; }
          })().toLowerCase();
          return (
            (o.displayName || '').toLowerCase().includes(q) ||
            (o.id || '').toLowerCase().includes(q) ||
            dateStr.includes(q)
          );
        });
    const withdrawals = filtered.filter(o => o.isWithdrawal).slice(0, 20);
    const deposits = filtered.filter(o => !o.isWithdrawal).slice(0, 20);
    const isWithdrawals = recentTab === 'withdrawals';
    const list = isWithdrawals ? withdrawals : deposits;
    return (
      <>
        <Grid.Col span={12}>
          <Divider my="sm" />
        </Grid.Col>
        <Grid.Col span={12}>
          <Group position="apart" align="center" style={{ marginBottom: 4 }}>
            <Title order={6}>
              {isWithdrawals ? 'Recent Withdrawals Containing This Item' : 'Recent Deposits Containing This Item'}
            </Title>
            <SegmentedControl
              value={recentTab}
              onChange={(v: 'withdrawals' | 'deposits' | string) => setRecentTab((v as any) as ('withdrawals' | 'deposits'))}
              data={[
                { label: 'Withdrawals', value: 'withdrawals' },
                { label: 'Deposits', value: 'deposits' },
              ]}
            />
          </Group>
          <MantineTextInput
            placeholder="Search recent orders by name or date..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            styles={{ input: { maxWidth: 420 } }}
            style={{ marginBottom: 8 }}
          />
          <div style={{display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', overflowX: 'hidden', maxHeight: '20vw'}}>
            {list.length === 0 && (
              <Box style={{ color: '#666', fontSize: 14 }}>No matching orders.</Box>
            )}
            {list.map(o => {
              const toHref = isWithdrawals ? `/staff/withdrawals/${o.id}` : `/staff/deposits/${o.id}`;
              return (
                <Link key={o.id} to={toHref} style={{ textDecoration: 'none' }}>
                  <Box style={{
                    padding: '8px 12px',
                    border: '1px solid #eee',
                    borderRadius: 6,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{display: 'flex', flexDirection: 'column'}}>
                      <span style={{fontWeight: 600}}>{o.displayName}</span>
                      <span style={{fontSize: 12, color: '#666'}}>{format(new Date(o.submittedAt), 'M/d/yyyy h:mm a')}</span>
                    </div>
                    <div style={{fontWeight: 600}}>Qty: {o.quantity}</div>
                  </Box>
                </Link>
              );
            })}
          </div>
        </Grid.Col>
      </>
    );
  };

  const renderDropDown = (setValues:  (values: React.SetStateAction<Item>, shouldValidate?: (boolean | undefined)) => void) => {
    if (!selectable || isReadOnly) return null;
    return (
      <Box style={{ marginBottom: '0.75rem', overflow: 'auto' }}>
        <Select
          options={dropDownOptions}
          backspaceRemovesValue={true}
          onChange={(newValue) => setValues({...getItemById(newValue?.value as any as string ?? '')})}
          onFocus={(e) => e.target.select()}
          openMenuOnFocus={false}
          openMenuOnClick={false}
          isClearable={true}
          isSearchable={true}
          menuShouldScrollIntoView={true}
          placeholder={'Search an item to move and / or edit...'}
          styles={{
            menuPortal: base => ({ ...base, zIndex: 3000, overflowY: 'visible', lineHeight: '0.5rem', transform: 'translate(0, -8px)'}),
            menu: base => ({...base, width: 'max-content', minWidth: '100%'}),
            container: base => ({...base, width: '100%'})

          }}
          menuPortalTarget={document.querySelector('body')}
          components={{}}
        />
      </Box>
    );
  };

  const formId = 'item-modal-form';
  return (
    <StandardModal
      opened={show}
      onCancel={onCancel}
      title={title}
      size={'lg'}
      submitText={'Submit'}
      cancelText={isReadOnly ? 'Close' : 'Cancel'}
      submitDisabled={isSubmitting || isReadOnly}
      showSubmit={!isReadOnly}
      loading={isSubmitting}
      formId={formId}
      noBodyScroll={false}
    >
      <Formik initialValues={existing ?? makeItem()} validationSchema={ItemModalSchema} onSubmit={() => undefined}>
        {({values, validateForm, handleSubmit, setFieldValue, setValues, resetForm}) => {
          return (
            <Form id={formId} onSubmit={(e) => processSubmit(e, values, validateForm, handleSubmit, resetForm)}>
              <Box style={{ width: '100%' }}>
                {renderDropDown(setValues)}

                <Grid gutter="md">
                  <Grid.Col span={6}>
                    <MantineTextInput
                      label="Name"
                      required
                      value={values.name}
                      disabled={isReadOnly}
                      onChange={(e) => setFieldValue(getFieldName('name'), e.currentTarget.value)}
                    />
                  </Grid.Col>

                  <Grid.Col span={6}>
                    <MantineSelect
                      data={categoryDropDownOptions as any}
                      value={(predeterminedCategoryId ?? values.categoryId ?? null) as any}
                      onChange={(val) => setFieldValue(getFieldName('categoryId'), val ?? '')}
                      searchable
                      clearable={!predeterminedCategoryId}
                      disabled={isReadOnly || !!predeterminedCategoryId}
                      withinPortal
                      label={"Category"}
                    />
                  </Grid.Col>

                  <Grid.Col span={6}>
                    <MantineNumberInput
                      label="Warehouse Quantity"
                      min={0}
                      value={values.warehouseQuantity}
                      disabled={isReadOnly}
                      onChange={(v) => setFieldValue(getFieldName('warehouseQuantity'), typeof v === 'number' ? v : 0)}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <MantineNumberInput
                      label="Store Quantity"
                      min={0}
                      value={values.storeQuantity}
                      disabled={isReadOnly}
                      onChange={(v) => setFieldValue(getFieldName('storeQuantity'), typeof v === 'number' ? v : 0)}
                    />
                  </Grid.Col>

                  <Grid.Col span={6}>
                    <MantineSwitch
                      label="Restrict Order Size"
                      checked={values.restrictOrderSize}
                      disabled={isReadOnly}
                      onChange={(e) => setFieldValue(getFieldName('restrictOrderSize'), e.currentTarget.checked)}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <MantineNumberInput
                      label="Maximum Order Size"
                      min={0}
                      disabled={isReadOnly || !values.restrictOrderSize}
                      value={values.restrictOrderSize ? values.maxOrderSize : 0}
                      onChange={(v) => setFieldValue(getFieldName('maxOrderSize'), typeof v === 'number' ? v : 0)}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <MantineNumberInput
                      label="Estimated Unit Cost"
                      precision={2}
                      min={0}
                      step={0.01}
                      icon={<span style={{fontSize: 14}}>$</span>}
                      value={centsToDollars(values.estimatedCost ?? 0)}
                      disabled={isReadOnly}
                      onChange={(v) => setFieldValue(getFieldName('estimatedCost'), dollarsToCents(typeof v === 'number' ? v : 0))}
                    />
                  </Grid.Col>
                  {renderRecentOrders()}
                  <Grid.Col span={12}>
                    {!isNullOrWhitespace(errorMessage) && (
                      <MantineAlert color="red" variant="light">
                        {errorMessage}
                      </MantineAlert>
                    )}
                  </Grid.Col>
                </Grid>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </StandardModal>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({actions: bindActionCreators({
    upsertItem: itemStore.actions.upsert
  }, dispatch)});
const mapStateToProps = (state: CommonState) => ({
  inventory: itemStore.selectors.getAsArray(state),
  alphabeticallySortedItems: itemStore.selectors.alphabeticallySortedItems(state),
  getItemById: itemStore.selectors.getById(state),
  categoryDropDownOptions: convertToDropDownOptions(categoryStore.selectors.getAsArray(state), propertyOf<Category>('name')),
  orders: orderStore.selectors.getAsArray(state)
});
export default connect(mapStateToProps, mapDispatchToProps)(ItemModal);
